import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PublicHeader from "@/components/layout/PublicHeader";
import PublicFooter from "@/components/layout/PublicFooter";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fileUrl } from "@/lib/files";
import { Package, MapPin, Building2, Award } from "lucide-react";
import RatingStars from "@/components/RatingStars";
import TierBadge from "@/components/TierBadge";

export default function SupplierStorefront() {
  const { sid } = useParams();
  const { t } = useTranslation();
  const [supplier, setSupplier] = useState(null);
  const [reviews, setReviews] = useState([]);
  useEffect(() => {
    api.get(`/suppliers/${sid}`).then((r) => setSupplier(r.data)).catch(() => {});
    api.get(`/suppliers/${sid}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, [sid]);

  if (!supplier) {
    return (
      <div className="min-h-screen bg-background">
        <PublicHeader />
        <div className="max-w-5xl mx-auto px-6 py-24 text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const displayName = supplier.company_name || supplier.name;
  const initials = displayName.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div data-testid="supplier-storefront" className="min-h-screen bg-background">
      <PublicHeader />

      {/* Hero */}
      <section className="bg-secondary text-secondary-foreground">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
          <div className="flex items-start gap-6 flex-wrap">
            <div className="w-24 h-24 rounded-lg bg-primary text-primary-foreground grid place-items-center font-heading font-black text-4xl">{initials}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <TierBadge tier={supplier.tier} size="md" testid="storefront-tier" />
              </div>
              <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight" data-testid="supplier-name">{displayName}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-secondary-foreground/80">
                {supplier.country && (
                  <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-primary" />{supplier.country}</span>
                )}
                <span className="inline-flex items-center gap-1.5"><Package className="w-4 h-4 text-primary" />{supplier.product_count} listed products</span>
                {supplier.member_since && (
                  <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4 text-primary" />Member since {new Date(supplier.member_since).getFullYear()}</span>
                )}
              </div>
              <div className="mt-4"><RatingStars value={supplier.rating?.avg || 0} count={supplier.rating?.count || 0} size="md" testid="supplier-rating" /></div>
            </div>
            <div className="flex flex-col gap-2">
              <Link to={`/messages?to=${supplier.id}`}><Button data-testid="message-supplier-btn" size="lg">Message supplier</Button></Link>
              <Link to="/register"><Button variant="secondary" className="bg-white text-secondary hover:bg-white/90">Sign up to buy</Button></Link>
            </div>
          </div>
          {supplier.bio && (
            <p className="mt-8 max-w-3xl text-secondary-foreground/80 leading-relaxed">{supplier.bio}</p>
          )}
          {supplier.certifications?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2">
              {supplier.certifications.map((c) => (
                <span key={c} className="inline-flex items-center gap-1.5 bg-white/10 text-primary border border-primary/30 rounded-md px-3 py-1.5 text-xs font-semibold">
                  <Award className="w-3 h-3" />{c}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-secondary">Products from {displayName}</h2>
          <div className="text-sm text-muted-foreground">{supplier.product_count} listing{supplier.product_count === 1 ? "" : "s"}</div>
        </div>
        {supplier.products?.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="supplier-no-products">This supplier has no live listings yet.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {supplier.products.map((p) => (
              <Link to={`/products/${p.id}`} key={p.id} className="bg-white border border-border rounded-lg overflow-hidden card-lift" data-testid={`sup-product-${p.id}`}>
                <div className="aspect-[16/10] bg-muted grid place-items-center overflow-hidden">
                  {p.image_urls?.[0] || p.image_ids?.[0] ? <img src={p.image_urls?.[0] || fileUrl(p.image_ids[0])} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-10 h-10 text-muted-foreground" />}
                </div>
                <div className="p-5">
                  <div className="text-xs overline text-muted-foreground">{p.category}</div>
                  <div className="mt-1 font-heading font-bold text-secondary">{p.name}</div>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.description}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm text-secondary font-medium">{p.price_min ? `${p.price_min}–${p.price_max || "?"} ${p.currency}/${p.unit}` : "On request"}</div>
                    <Badge variant="outline">MOQ {p.moq}</Badge>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Behind the scenes gallery */}
      {supplier.gallery_urls?.length > 0 && (
        <section
          data-testid="supplier-gallery"
          className="bg-[#faf8f4] border-y border-border/70"
        >
          <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="max-w-2xl mx-auto text-center mb-14">
              <div className="overline text-primary mb-3">{t("storefront.gallery.eyebrow")}</div>
              <h2 className="font-heading text-3xl md:text-5xl font-black tracking-tight text-secondary">
                {t("storefront.gallery.h")}
              </h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">
                {t("storefront.gallery.sub", { brand: supplier.company_name || supplier.name })}
              </p>
            </div>

            {/* Uniform 4-col editorial grid — all tiles equal, soft-shadowed for premium press-kit feel. */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5" data-testid="supplier-gallery-grid">
              {supplier.gallery_urls.slice(0, 8).map((url, i) => (
                <figure
                  key={i}
                  className="relative aspect-square overflow-hidden rounded-xl bg-muted ring-1 ring-black/5 shadow-sm hover:shadow-xl transition-all duration-500 group"
                  data-testid={`gallery-photo-${i}`}
                >
                  <img
                    src={url}
                    alt=""
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                    onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                  />
                  {/* soft bottom vignette on hover for editorial polish */}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reviews */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        <div className="flex items-baseline justify-between mb-6">
          <h2 className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight text-secondary">Buyer reviews</h2>
          <div className="text-sm text-muted-foreground">{supplier.rating?.count || 0} review{(supplier.rating?.count || 0) === 1 ? "" : "s"}</div>
        </div>
        {reviews.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-sm text-muted-foreground" data-testid="supplier-no-reviews">No reviews yet — be the first buyer to work with this supplier.</CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {reviews.map((r) => (
              <Card key={r.id} className="card-lift" data-testid={`review-${r.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <CardTitle className="font-heading tracking-tight text-lg">{r.title || `${r.rating}-star review`}</CardTitle>
                      <div className="text-xs text-muted-foreground mt-1">{r.buyer_name}{r.buyer_country ? ` · ${r.buyer_country}` : ""} · {new Date(r.created_at).toLocaleDateString()}</div>
                    </div>
                    <RatingStars value={r.rating} showCount={false} />
                  </div>
                </CardHeader>
                {r.body && <CardContent><p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">{r.body}</p></CardContent>}
              </Card>
            ))}
          </div>
        )}
      </section>

      <PublicFooter />
    </div>
  );
}
